import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Menu, Bell, LogOut } from 'lucide-react';
import { CRMProvider } from './context/CRMContext';
// import RoleSwitcher from './components/RoleSwitcher'; // <-- We don't need this anymore!
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient'; 
import SignalsPage from './pages/SignalsPage';

// 1. Import Components
import Sidebar from './components/Sidebar';
import GlobalOverlay from './components/GlobalOverlay';
import Login from './components/Login';
// 2. Import Pages
import DashboardPage from './pages/Dashboard';
import ClientsPage from './pages/Clients';
import PartnersPage from './pages/Partners';
import MarketingPage from './pages/Marketing';
import CompliancePage from './pages/Compliance';
import StaffPage from './pages/Staff';
import AdminPage from './pages/Admin';
import AcademyAdmin from './pages/AcademyAdmin';
import TeamHub from './pages/TeamHub';
import ClientProfile from './components/ClientProfile';
import FinancePage from './pages/FinancePage';
import InvestmentAdmin from './pages/InvestmentAdmin';
import FundedAccountsPage from './pages/FundedAccountsPage';

const App = () => {
  // --- NEW AUTH STATE ---
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null); // Will hold the real DB profile
  
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation(); 
  const navigate = useNavigate();
  // --- COMMS HUB STATE ---
  const [commsHistory, setCommsHistory] = useState([]);
  const [unreadCommsCount, setUnreadCommsCount] = useState(0);
  const [isCommsOpen, setIsCommsOpen] = useState(false);

  // --- SUPABASE AUTH LISTENER (WITH 2FA GATEKEEPER) ---
  useEffect(() => {
    const handleSessionChange = async (sessionObj) => {
      if (!sessionObj) {
        setSession(null);
        setCurrentUser(null);
        setIsLoading(false);
        return;
      }

      try {
        // 1. Check the Enterprise Security Policy
        const { data: settings } = await supabase.from('crm_settings').select('enforce_2fa').eq('id', 'global').maybeSingle();
        
        if (settings?.enforce_2fa) {
          // 2. Check if the user has passed the 2FA check (AAL2)
          const { data: { currentLevel } } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          
          if (currentLevel !== 'aal2') {
            // STOP! They only entered a password. Do NOT load the CRM profile.
            // By returning early, we force App.jsx to keep showing <Login />
            setIsLoading(false);
            return;
          }
        }

        // 3. Security Cleared! (Either 2FA is off, or they passed the 6-digit check)
        setSession(sessionObj);
        fetchUserProfile(sessionObj.user.id);
        
      } catch (err) {
        console.error("Auth security check failed:", err);
        setIsLoading(false);
      }
    };

    // Run on initial load
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSessionChange(session);
    });

    // Run whenever they log in or verify 2FA
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSessionChange(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // --- FETCH REAL ROLE FROM DATABASE ---
  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, role')
        .eq('id', userId)
        .single();

      if (error) throw error;

      // Map it to exactly what your App already expects!
      if (data) {
        setCurrentUser({
          id: data.id,
          name: data.full_name || 'Staff Member',
          role: data.role || 'Support_L1'
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

// --- FETCH & LISTEN TO COMMS HUB ---
  useEffect(() => {
    if (!currentUser) return;

    const loadComms = async () => {
      // 1. Fetch the last 24 hours of broadcasts
      const yesterday = new Date();
      yesterday.setHours(yesterday.getHours() - 24);

      const { data: pastMsgs } = await supabase
        .from('crm_broadcasts')
        .select('*')
        .gte('created_at', yesterday.toISOString())
        .order('created_at', { ascending: false });

      if (pastMsgs) {
        const relevantMsgs = pastMsgs.filter(msg => {
          if (msg.target_type === 'all') return true;
          if (msg.target_type === 'role' && currentUser.role && currentUser.role.toLowerCase().includes(msg.target_value.toLowerCase())) return true;
          if (msg.target_type === 'user' && msg.target_value === currentUser.id) return true;
          return false;
        });
        setCommsHistory(relevantMsgs);
      }
    };

    loadComms();

    // 2. Listen for NEW messages to increment the badge
    const channel = supabase
      .channel('app-topbar-comms')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'crm_broadcasts' }, (payload) => {
        const newMsg = payload.new;
        let shouldShow = false;
        
        if (newMsg.target_type === 'all') shouldShow = true;
        else if (newMsg.target_type === 'role' && currentUser.role && currentUser.role.toLowerCase().includes(newMsg.target_value.toLowerCase())) shouldShow = true;
        else if (newMsg.target_type === 'user' && newMsg.target_value === currentUser.id) shouldShow = true;

        if (shouldShow) {
          setCommsHistory(prev => [newMsg, ...prev]);
          if (!isCommsOpen) setUnreadCommsCount(prev => prev + 1);
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [currentUser, isCommsOpen]);

  const toggleCommsHub = () => {
    setIsCommsOpen(!isCommsOpen);
    if (!isCommsOpen) setUnreadCommsCount(0); // Clear badge on open
  };

 const handleLogout = async () => {
    if (currentUser) {
       // Mark them offline before logging out
       await supabase.from('profiles').update({ status: 'offline' }).eq('id', currentUser.id);
    }
    await supabase.auth.signOut();
  };

// ==========================================
  // --- ENTERPRISE SESSION TIMEOUT MONITOR ---
  // ==========================================
  const lastActivityTime = useRef(Date.now());
  const [timeoutMinutes, setTimeoutMinutes] = useState(30); // Default fallback

  useEffect(() => {
    if (!currentUser) return;

    // 1. Fetch the strict timeout limit from the database
    const fetchTimeout = async () => {
      const { data } = await supabase.from('crm_settings').select('session_timeout').eq('id', 'global').maybeSingle();
      if (data && data.session_timeout) {
        console.log(`[Security] Session timeout set to ${data.session_timeout} minutes.`);
        setTimeoutMinutes(data.session_timeout);
      }
    };
    fetchTimeout();

    // 2. Function to reset the timer when the user interacts
    const updateActivity = () => {
      lastActivityTime.current = Date.now();
    };

    // Listen for all typical user interactions
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    // 3. The background checker (Runs every 10 seconds for high accuracy)
    const intervalId = setInterval(() => {
      const currentTime = Date.now();
      const timeIdle = currentTime - lastActivityTime.current;
      const maxIdleTime = timeoutMinutes * 60 * 1000; // Convert minutes to ms

      // Print a debug message to the console so we can watch it work!
      console.log(`[Security] Idle for ${Math.floor(timeIdle / 1000)}s / Max: ${timeoutMinutes * 60}s`);

      if (timeIdle > maxIdleTime) {
        console.log(`[Security] Maximum idle time exceeded. Booting user...`);
        handleLogout(); 
      }
    }, 10000); // 10000ms = 10 seconds

    // Cleanup listeners when the component unmounts
    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
      clearInterval(intervalId);
    };
  }, [currentUser, timeoutMinutes]);
  
  // ==========================================

  const currentPath = location.pathname === '/' ? 'dashboard' : location.pathname.split('/')[1];

  const handleTabChange = (tabName) => {
      if (tabName === 'dashboard') navigate('/');
      else navigate(`/${tabName}`);
      setSidebarOpen(false);
  };

  // --- GATEKEEPER: SHOW LOGIN OR LOADER ---
  if (isLoading) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-blue-400">Loading Floor Data...</div>;
  }

  if (!session || !currentUser) {
    return <Login />;
  }

  // --- THE MASTER APP RENDER (Almost untouched!) ---
  return (
    <CRMProvider>
      <div className="flex bg-slate-950 min-h-screen font-sans text-slate-200 selection:bg-amber-500/30">
        
        <Sidebar activeTab={currentPath} setActiveTab={handleTabChange} isOpen={isSidebarOpen} setIsOpen={setSidebarOpen} userRole={currentUser.role} />
        
        <div className="md:ml-72 flex-1 relative w-full">
          <header className="bg-slate-950/80 backdrop-blur-md h-20 border-b border-slate-800/50 px-4 md:px-8 flex justify-between items-center sticky top-0 z-40">
            <div className="flex items-center space-x-4">
               <button onClick={() => setSidebarOpen(true)} className="md:hidden text-slate-400 hover:text-white p-2">
                 <Menu size={24} />
               </button>
               <h2 className="text-lg md:text-xl font-bold text-white capitalize tracking-tight">{currentPath}</h2>
            </div>
            <div className="flex items-center space-x-4 relative">
               
               {/* --- LIVE COMMS HUB TOGGLE --- */}
               <div className="relative">
                 <button 
                    onClick={toggleCommsHub}
                    className={`p-2 rounded-full transition-all relative ${isCommsOpen ? 'bg-blue-600 text-white' : 'bg-slate-900 hover:bg-slate-800 text-slate-400'}`}
                 >
                    <Bell size={20} />
                    {/* The Red Unread Badge */}
                    {unreadCommsCount > 0 && !isCommsOpen && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-slate-950 animate-pulse">
                            {unreadCommsCount}
                        </span>
                    )}
                 </button>

                 {/* --- THE DROPDOWN INBOX PANEL --- */}
                 {isCommsOpen && (
                    <div className="absolute top-full right-0 mt-4 w-80 bg-slate-900 border border-slate-700 shadow-2xl rounded-xl overflow-hidden z-[100] animate-in slide-in-from-top-2 fade-in duration-200">
                        <div className="bg-slate-800 p-3 border-b border-slate-700 flex justify-between items-center">
                            <h4 className="font-bold text-white text-sm">Floor Comms</h4>
                            <span className="text-[10px] text-slate-400">Last 24h</span>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto p-2 flex flex-col gap-2 custom-scrollbar">
                            {commsHistory.length > 0 ? commsHistory.map((msg, i) => (
                                <div key={i} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 hover:border-blue-500/30 transition-colors">
                                    <p className="text-sm text-slate-200 mb-2 leading-snug">{msg.message}</p>
                                    <div className="flex justify-between items-center text-[10px]">
                                        <span className="text-blue-400 font-bold">{msg.sender_name || msg.sender_email}</span>
                                        <span className="text-slate-500">{new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center p-6 text-slate-500 text-xs">Queue is clear.</div>
                            )}
                        </div>
                    </div>
                 )}
               </div>
               
               {/* Profile Info & Logout */}
               <div className="hidden sm:flex items-center gap-3">
                 <div className="text-right">
                    <p className="text-sm font-bold text-white leading-tight">{currentUser.name}</p>
                    <p className="text-[10px] text-blue-400 uppercase tracking-wider">{currentUser.role.replace('_', ' ')}</p>
                 </div>
               </div>
               
               <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center font-bold text-white text-xs">
                    {currentUser.name.charAt(0)}
               </div>

               <button onClick={handleLogout} className="p-2 text-slate-500 hover:text-red-400 transition-colors ml-2" title="Sign Out">
                 <LogOut size={18} />
               </button>
            </div>
          </header>

          <main className="min-h-[calc(100vh-80px)] w-full overflow-x-hidden">
             {/* YOUR EXACT ROUTES GO HERE UNCHANGED */}
             <Routes>
                <Route path="/" element={<DashboardPage userRole={currentUser.role} />} />
                <Route path="/clients" element={<ClientsPage userRole={currentUser.role} currentUser={currentUser} />} />
                <Route path="/partners" element={<PartnersPage currentUser={currentUser} />} />
                <Route path="/marketing" element={<MarketingPage />} />
                <Route path="/compliance" element={<CompliancePage />} />
                <Route path="/staff" element={<StaffPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/academy" element={<AcademyAdmin />} />
                <Route path="/team-hub" element={<TeamHub currentUser={currentUser} role={currentUser.role} />} />
                <Route path="/client/:id" element={<ClientProfile userRole={currentUser.role} currentUser={currentUser} />} />
                
                {/* ---> NEW: SIGNALS PAGE & FINANCE PAGE <--- */}
                <Route path="/signals" element={<SignalsPage userRole={currentUser.role} userName={currentUser.name} />} />
                <Route path="/finance" element={<FinancePage />} />
                <Route path="/investment-admin" element={<InvestmentAdmin />} />
                <Route path="/funded-accounts" element={<FundedAccountsPage userRole={currentUser.role} />} />
                
                {/* The Catch-All MUST be the absolute last item! */}
                <Route path="*" element={<Navigate to="/" replace />} />
             </Routes>
          </main>
        </div>
        <GlobalOverlay userRole={currentUser.role} currentUser={currentUser} />
      </div>
    </CRMProvider>
  );
};

export default App;